import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import InventoryIcon from "@mui/icons-material/Inventory";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  latestInventoryDate,
  useStrategyDashboardInventory,
} from "components/strategyDashboard/inventory/inventoryHooks";
import { defaultMaterialGrouping } from "components/strategyDashboard/materialGroupings";
import { InventoryTable } from "components/strategyDashboard/inventory/InventoryTable";
import { GroupedInventorySeries } from "components/strategyDashboard/types";
import { Loaded } from "models/loaded";
import { OverviewCard } from "./OverviewCard";

dayjs.extend(utc);

const INVENTORY_LOOKBACK_WEEKS = 2;

export const LatestInventory = ({ timestamp }: { timestamp: Dayjs }) => {
  const { t } = useTenantTranslation();
  const tenant = useTenant();

  const end = timestamp.toISOString();
  const start = timestamp
    .subtract(INVENTORY_LOOKBACK_WEEKS, "week")
    .toISOString();

  const loadedInventory = useStrategyDashboardInventory(start, end);

  const latestDate =
    loadedInventory.status === "success"
      ? latestInventoryDate(loadedInventory.data)
      : null;

  const latestDateFormatted = latestDate
    ? dayjs.utc(latestDate).format("LL")
    : "-";

  const isFetching =
    loadedInventory.status === "success" ? loadedInventory.fetching : false;

  const headerContent = (
    <Stack textAlign="right" sx={{ ml: "auto" }}>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {t("latestInventoryReport")}
      </Typography>
      {isFetching ? (
        <Skeleton variant="text" width={80} />
      ) : (
        <Typography variant="body2" sx={{ fontWeight: "fontWeightSemiBold" }}>
          {latestDateFormatted}
        </Typography>
      )}
    </Stack>
  );

  return (
    <OverviewCard
      title={t("inventory")}
      icon={InventoryIcon}
      headerContent={headerContent}
      buttonText={t("viewFullInventory")}
      buttonHref={`/${tenant}/dashboard/inventory`}
    >
      <InventoryContent
        loadedInventory={loadedInventory}
        latestDate={latestDate}
      />
    </OverviewCard>
  );
};

const InventoryContent = ({
  loadedInventory,
  latestDate,
}: {
  loadedInventory: Loaded<GroupedInventorySeries>;
  latestDate: Date | null;
}) => {
  const { t } = useTenantTranslation();

  const isFetching =
    loadedInventory.status === "success" ? loadedInventory.fetching : false;
  const isLoading =
    loadedInventory.status === "loading" ||
    loadedInventory.status === "waiting";

  if (isLoading || isFetching) {
    return <Skeleton variant="rectangular" height={250} />;
  }

  if (loadedInventory.status !== "success") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          {t("noData")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "94%",
        minHeight: 0,
        px: 2,
        // Unbounded, the table grows to its rows and pushes the total row out of the card.
        "& > div": { height: "100%", minHeight: 0 },
      }}
    >
      <InventoryTable
        inventorySeries={loadedInventory.data}
        selectedDate={latestDate}
        selectedMaterialGrouping={defaultMaterialGrouping(
          loadedInventory.data.availableMaterialGroupings
        )}
      />
    </Box>
  );
};
